import { PatientConsentContext } from '@/context/PatientConsentProvider';
import { BrandThemeProvider } from '@montugroup/themes';
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import type { Theme } from '@mui/material';
import { SwipeableDrawer, styled } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { alternaleafAppBarConfig } from '@/components/layout/navbar/navbar.config';
import { NavMenuItem, NavMenuLink } from '@montugroup/circuit-shared';
import AltLogo from '@montugroup/circuit-shared/components/common/AltLogo';
import { tmpSxSpecificityForce } from '@montugroup/circuit-shared/theme';

import { usePortalPartnerBrand } from '@/hooks/portal-partner-brand';
import { useCreateOrderButton } from '@/hooks/useCreateOrderButton';
import useCustomer from '@/hooks/user/useCustomer';
import { HOME_ROUTE } from '@/routes/route-paths';
import { Box, Button, IconButton, List } from '@montugroup/design-system';
import Logout from './Logout';

interface AppDrawerProps {
  open?: boolean;
}

const DRAWER_WIDTH = 256;
const DRAWER_WIDTH_COLLAPSED = 77;
const VIEW_ORDERS_MENU_ITEM_ID = 4;

const openedMixin = (theme: Theme) => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: DRAWER_WIDTH_COLLAPSED,
});

const AppDrawer = styled(SwipeableDrawer)<AppDrawerProps>(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
  }),
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    ...(open ? openedMixin(theme) : closedMixin(theme)),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const NavContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(14),
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}));

const ListContainer = styled(List)`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-bottom: 0;
`;

export const OrderButton = styled(Button)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: `${theme.palette.common.white} ${tmpSxSpecificityForce}`,
  padding: `${theme.spacing(4, 0)} ${tmpSxSpecificityForce}`,
  width: '100%',
  gap: theme.spacing(2),
  boxShadow: theme.shadows[2],
  '&:hover': {
    color: `${theme.palette.primary.main} ${tmpSxSpecificityForce}`,
    backgroundColor: `${theme.palette.grey[200]} ${tmpSxSpecificityForce}`,
  },
  '&:focus': {
    outline: 'none',
  },
}));

const StickyHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: theme.palette.primary.main,
  paddingTop: theme.spacing(10),
  paddingLeft: theme.spacing(4.2),
  display: 'flex',
  alignItems: 'center',
}));

const StickyFooter = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: 0,
  left: 0,
  zIndex: 1,
  backgroundColor: theme.palette.primary.main,
  paddingTop: theme.spacing(2),
  marginTop: 'auto',
}));

const OrderButtonWrapper = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(6),
  marginBottom: theme.spacing(6),
  marginRight: theme.spacing(4),
  marginLeft: theme.spacing(4),
  width: 'auto',
}));

function Sidebar() {
  const { key: partnerKey } = usePortalPartnerBrand();
  const createOrderButton = useCreateOrderButton();
  const [open, setOpen] = useState<boolean>(true);
  const [textVisible, setTextVisible] = useState(true);

  const { data: customer } = useCustomer();
  const isDischarged = customer?.isDischarged;

  const { requiresConsentSubmission } = useContext(PatientConsentContext);
  const showOrderButton = !isDischarged && !requiresConsentSubmission;

  // Consent filtering
  const mainMenuItemsAlternaleaf = alternaleafAppBarConfig.filter((menuItem) => {
    const shouldFilter = !showOrderButton && menuItem.id === VIEW_ORDERS_MENU_ITEM_ID;
    return !shouldFilter;
  });

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => setTextVisible(true), 200);
    } else {
      setTextVisible(false);
    }
  }, [open]);

  return (
    <AppDrawer variant="permanent" open={open} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
      <StickyHeader>
        <IconButton
          aria-hidden
          onClick={() => setOpen((prevState) => !prevState)}
          sx={{
            color: (theme) => theme.palette.common.white,
            '&:focus': {
              outline: 'none',
            },
          }}
        >
          {open ? <KeyboardDoubleArrowLeft /> : <KeyboardDoubleArrowRight />}
        </IconButton>
      </StickyHeader>

      <NavContainer component="nav">
        <Box
          pl={open ? 7 : 4.2}
          pb={3}
          sx={{
            transition: '0.3s',
          }}
        >
          <NavMenuLink aria-label="Home" to={HOME_ROUTE} onClick={scrollToTop}>
            <AltLogo height="24" />
          </NavMenuLink>
        </Box>

        <ListContainer role="menubar">
          {mainMenuItemsAlternaleaf.map((mainMenuItem) => (
            <NavMenuItem
              key={mainMenuItem.id}
              toggleText={open}
              menuItem={mainMenuItem}
              onClick={scrollToTop}
              isSideBar
            />
          ))}
        </ListContainer>
        <StickyFooter>
          {showOrderButton && (
            <BrandThemeProvider theme={partnerKey}>
              <OrderButtonWrapper>
                <Link
                  to={createOrderButton.link}
                  state={{
                    destinationPortal: 'customer',
                  }}
                  style={{
                    width: '100%',
                  }}
                >
                  <OrderButton variant="contained" size="large">
                    {createOrderButton.icon && <createOrderButton.icon />}
                    {textVisible && createOrderButton.name}
                  </OrderButton>
                </Link>
              </OrderButtonWrapper>
            </BrandThemeProvider>
          )}
          <Logout toggleText={open} />
        </StickyFooter>
      </NavContainer>
    </AppDrawer>
  );
}

export default Sidebar;
